import { downloadFile } from './downloadFile'

import { showError, showSuccess } from '@/notifications'
import { useBackgroundTaskStore } from '@/stores/backgroundTask.store'
import { ISectionsParams, IServerSection } from '@/types/sections.type'
import { IServerFields } from '@/types/serverField.type'

type RequestOptions = RequestInit

export interface Downloadable {
  downloadUrl?: string
  downloadFileName?: string
  print?: boolean | undefined
  actionResult?: {
    DownloadUrl?: string
  }
}

export interface Exportable {
  ExportUrl?: string
}

export interface ApiResponse<T = any> {
  done: boolean
  goto?: string
  refresh?: boolean
  msg?: string
  msgs?: string[] | null
  msgsErrors?: Record<string, string> | null
  errors?: string[]
  data?: T & Downloadable & Exportable
  fields?: IServerFields | null
  params?: any
  sectionsParams?: ISectionsParams
  sections?: IServerSection[]
}

export interface IHandlers {
  onDownload?: (url: string, fileName: string) => void
}

class Request {
  private static readonly ERROR_CODE_FORBIDDEN = 403
  private static readonly ERROR_CODE_UNAUTHORIZED = 401

  private static readonly ERROR_CODE_INTERNAL_SERVER = 500

  private defaultOptions: RequestOptions = {
    method: 'GET',
    headers: { Accept: 'application/json' }
  }

  private handleErrors (code: number): void {
    if (code === Request.ERROR_CODE_FORBIDDEN || code === Request.ERROR_CODE_UNAUTHORIZED) {
      setTimeout(() => location.reload(), 10 * 1000)
    }

    if (code === Request.ERROR_CODE_INTERNAL_SERVER) {
      showError('Внутренняя ошибка сервера (500). Попробуйте позже.')
      throw new Error('Server error 500')
    }
  }

  private prepareOptions (opts?: RequestOptions): RequestOptions {
    return { ...this.defaultOptions, ...opts }
  }

  private async prepareResponse (response: Response): Promise<ApiResponse> {
    this.handleErrors(response.status)

    try {
      return await response.json()
    } catch {
      throw new Error('Ошибка парсинга ответа от сервера')
    }
  }

  private showErrors (errors: string[]): void {
    errors.forEach(error => showError(error))
  }

  private showSuccessMessages (messages: string[]): void {
    messages.forEach(message => showSuccess(message))
  }

  private handleResponse<T> (response: ApiResponse<T>, silent: boolean, handlers: IHandlers): ApiResponse<T> {
    const messages: string[] = []
    const errorMessages: string[] = []

    // Если сервер вернул backgroundTaskId — сохраняем в store для отслеживания через SSE
    const rawData = response.data as Record<string, unknown> | undefined
    if (rawData?.backgroundTaskId) {
      const backgroundTaskStore = useBackgroundTaskStore()
      backgroundTaskStore.setTaskId(String(rawData.backgroundTaskId))
    }

    // Если отправили файл на скачивание - скачаем/отправим на печать его
    let downloadUrl = response.data?.downloadUrl || response.data?.actionResult?.DownloadUrl

    if (handlers.onDownload) {
      downloadUrl = downloadUrl || response.data?.ExportUrl
    }

    const fileName = response.data?.downloadFileName ?? downloadUrl

    const needPrint = response.data?.print || false

    if (handlers.onDownload) {
      if (downloadUrl && fileName) {
        downloadUrl = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`
        handlers.onDownload(downloadUrl, fileName ?? '')
      }
    } else if (downloadUrl && fileName) {
      downloadUrl = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`
      if (needPrint) {
        window.open(downloadUrl, '_blank')
      } else {
        downloadFile(downloadUrl, fileName)
      }
    }

    if (silent) return response

    if (response.msg) messages.push(response.msg)
    if (response.msgs) messages.push(...(Object.values(response.msgs).length > 0 ? Object.values(response.msgs) : response.msgs))
    if (response.msgsErrors) errorMessages.push(...Object.values(response.msgsErrors))

    if (response.done === false && messages.length === 0 && errorMessages.length === 0) {
      errorMessages.push('Что-то пошло не так.')
    }

    if (messages.length > 0 || errorMessages.length > 0) {
      if (!response.done) {
        this.showErrors(messages)
        this.showErrors(errorMessages)
        throw new Error(messages[0] ?? errorMessages[0])
      } else {
        this.showSuccessMessages(messages)
      }
    }

    return response
  }

  public do = async <T = any>(url: string, opts?: RequestOptions, silent: boolean = false, handlers: IHandlers = {}): Promise<ApiResponse<T>> => {
    const options = this.prepareOptions(opts)
    const response = await fetch(url, options)
    const jsonResponse = await this.prepareResponse(response)

    return this.handleResponse<T>(jsonResponse, silent, handlers)
  }
}

export const request = new Request().do
